import { Student } from '../modules/my-groups/components/vo/import-student-modal/Verify';
import { validateEmail } from './emailValidator';

const numberRegex = /[0-9]*$/;

function usernameBaseExists(username: string, existingUsernames: string[]) {
  return existingUsernames.some(un => {
    const usernameWithoutCount = un.replace(numberRegex, '');

    return username === usernameWithoutCount;
  });
}

/**
 * concats first and lastname with dot
 * replaces string and sets max length
 *
 * takes first 125 -- 4 characters are reserved for uniqueness
 */
export function buildUsernameBase(firstName: string, lastName: string) {
  return `${firstName.toString().trim().substring(0, 125).replaceAll(' ', '')}.${lastName
    .toString()
    .trim()
    .substring(0, 125)
    .replaceAll(' ', '')}`.toLowerCase();
}

/**
 * checks if username base already exists in course
 * searches for highest number after base
 * adds highest number + 1 after username base to make unique
 */
export function makeUsernameUnique(firstName: string, lastName: string, existingUsernames: string[]) {
  const usernameBase = buildUsernameBase(firstName, lastName);

  if (!usernameBaseExists(usernameBase, existingUsernames)) return usernameBase;

  const sameUsernames = existingUsernames.filter(un => {
    const usernameWithoutCount = un.replace(numberRegex, '');

    return usernameBase === usernameWithoutCount;
  });

  const highestCount = sameUsernames.reduce((highest, un) => {
    const result = numberRegex.exec(un);

    if (!result) return highest;

    const matchNumber = Number(result[0]);
    return highest - matchNumber < 0 ? matchNumber : highest;
  }, 1);

  return `${usernameBase}${highestCount + 1}`;
}

export function validateForm({
  firstName,
  lastName,
  username,
  password,
  email,
}: Student & { email?: string }): Partial<Record<keyof Student, string>> & { email?: string } {
  const VO_NAME_INVALID_CHARACTERS = /[0-9@]/;
  const VO_USERNAME_INVALID_CHARACTERS = /[@\sA-Z]/;
  const TRANSLATION_KEY = 'MODULES.GROUPS.MY_CLASSES.DIALOGS.STUDENTS.';

  const errors: ReturnType<typeof validateForm> = {};

  if (!firstName) errors.firstName = `${TRANSLATION_KEY}FORM.FIRST_NAME.ERRORS.REQUIRED`;
  else {
    if (!errors.firstName && firstName.length > 250)
      errors.firstName = `${TRANSLATION_KEY}FORM.FIRST_NAME.ERRORS.MAX_LENGTH`;
    if (!errors.firstName && VO_NAME_INVALID_CHARACTERS.test(firstName))
      errors.firstName = `${TRANSLATION_KEY}FORM.FIRST_NAME.ERRORS.FORMAT`;
  }

  if (!lastName) errors.lastName = `${TRANSLATION_KEY}FORM.LAST_NAME.ERRORS.REQUIRED`;
  else {
    if (!errors.lastName && lastName.length > 250)
      errors.lastName = `${TRANSLATION_KEY}FORM.LAST_NAME.ERRORS.MAX_LENGTH`;
    if (!errors.lastName && VO_NAME_INVALID_CHARACTERS.test(lastName))
      errors.lastName = `${TRANSLATION_KEY}FORM.LAST_NAME.ERRORS.FORMAT`;
  }

  if (email) {
    if (!errors.email && !validateEmail(email)) errors.email = `${TRANSLATION_KEY}FORM.EMAIL.ERRORS.FORMAT`;
  }

  if (!username) errors.username = `${TRANSLATION_KEY}FORM.USERNAME.ERRORS.REQUIRED`;
  else {
    if (!errors.username && username.length > 250)
      errors.username = `${TRANSLATION_KEY}FORM.USERNAME.ERRORS.MAX_LENGTH`;
    if (!errors.username && VO_USERNAME_INVALID_CHARACTERS.test(username))
      errors.username = `${TRANSLATION_KEY}FORM.USERNAME.ERRORS.FORMAT`;
  }

  if (!password) errors.password = `${TRANSLATION_KEY}FORM.PASSWORD.ERRORS.REQUIRED`;
  else {
    if (!errors.password && password.length < 4) errors.password = `${TRANSLATION_KEY}FORM.PASSWORD.ERRORS.MIN_LENGTH`;
    if (!errors.password && password.length > 50) errors.password = `${TRANSLATION_KEY}FORM.PASSWORD.ERRORS.MAX_LENGTH`;
    if (!errors.password && password.length !== password.trim().length)
      errors.password = `${TRANSLATION_KEY}FORM.PASSWORD.ERRORS.SPACE`;
  }

  return errors;
}
